import React, { useEffect, useState } from 'react';
import Spinner from '../Spinner';
import { ConferenceAffiliation } from '../../types/conferenceHistory';

export interface ConferenceAffiliationFormValues {
  conferenceName: string;
  conferenceTidyName: string;
  startYear: string;
  endYear: string;
}

interface ConferenceAffiliationFormModalProps {
  title: string;
  initial?: ConferenceAffiliation | null;
  isSubmitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (values: ConferenceAffiliationFormValues) => void;
}

function toFormValues(row?: ConferenceAffiliation | null): ConferenceAffiliationFormValues {
  if (!row) {
    return { conferenceName: '', conferenceTidyName: '', startYear: '', endYear: '' };
  }
  const currentYear = new Date().getFullYear();
  const endIsPresent = row.endYear === null || row.endYear >= currentYear;
  return {
    conferenceName: row.conferenceName?.trim() ?? '',
    conferenceTidyName: row.conferenceTidyName?.trim() ?? '',
    startYear: String(row.startYear),
    endYear: endIsPresent ? '' : String(row.endYear),
  };
}

function ConferenceAffiliationFormModal({
  title,
  initial,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit,
}: ConferenceAffiliationFormModalProps) {
  const [values, setValues] = useState<ConferenceAffiliationFormValues>(() => toFormValues(initial));

  useEffect(() => {
    setValues(toFormValues(initial));
  }, [initial]);

  const handleChange =
    (field: keyof ConferenceAffiliationFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex w-full max-w-lg flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {errorMessage ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {errorMessage}
            </div>
          ) : null}

          <label className="block text-sm font-medium text-slate-700">
            Conference name
            <input
              type="text"
              required
              value={values.conferenceName}
              onChange={handleChange('conferenceName')}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="e.g. Southeastern Conference"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Short name
            <input
              type="text"
              value={values.conferenceTidyName}
              onChange={handleChange('conferenceTidyName')}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="e.g. SEC"
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm font-medium text-slate-700">
              Start year
              <input
                type="number"
                required
                min={1800}
                max={2100}
                value={values.startYear}
                onChange={handleChange('startYear')}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm font-medium text-slate-700">
              End year
              <input
                type="number"
                min={1800}
                max={2100}
                value={values.endYear}
                onChange={handleChange('endYear')}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Leave empty for present"
              />
              <span className="mt-1 block text-xs text-slate-500">
                Leave empty to mark through the current year ({new Date().getFullYear()}).
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {isSubmitting ? <Spinner size="sm" /> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConferenceAffiliationFormModal;

export function formValuesToWriteBody(
  values: ConferenceAffiliationFormValues
): {
  conferenceName: string;
  conferenceTidyName?: string;
  startYear: number;
  endYear?: number | null;
} {
  const endTrim = values.endYear.trim();
  return {
    conferenceName: values.conferenceName.trim(),
    conferenceTidyName: values.conferenceTidyName.trim() || undefined,
    startYear: Number(values.startYear),
    endYear: endTrim === '' ? null : Number(endTrim),
  };
}
